<template>
  <div
    id="wrapper"
    :style="wrapStyle"
    @mousemove="interactiveMethods.onMousemove"
    @mousedown="interactiveMethods.onMousemove"
    @mouseup="interactiveMethods.onMouseup"
    @touchstart="interactiveMethods.onTouchMove"
    @touchmove="interactiveMethods.onTouchMove"
    @touchend="interactiveMethods.onTouchEnd"
  >
    <div class="lectureWrap" :class="{on : learningStore.isHideBarsToggle}">
      <LazyGlobalLoading v-if="loadingStore.show"/>
      <template v-else>
        <LazyLearningProSkillAssureHeader
            :organization-name="assessmentStore.proSkillAssure.myInfo.organizationName"
            :title="assessmentStore.proSkillAssure.assessment.title"
            :is-in-progress="true"
            :task-duration-tracking-service="taskDurationTrackingService"
        />
        <LazyWebcamIndicator
            :is-recording="webcamRecordingService.isRecording.value"
            style="position: fixed; top: 12px; right: 16px; z-index: 20;"
        />
        <LazyLearningMiddle ref="middle" class="assessmentMiddle" />
        <LazyLearningProSkillAssureFooter :is-in-progress="true" @on-click-submit="initMethods.getTaskData" />
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeMount, onBeforeUnmount, onMounted, provide, ref, useI18n, useRoute, useRouter, watch } from "#imports";
import { useUserStore } from "~/store/user/userStore";
import { useLearningStore } from "~/store/learning/learningStore";
import {useLearningAssessmentStore} from "~/store/learning/assessment/learningAssessmentStore";
import { onBeforeRouteLeave } from "vue-router";
import { useDialogStore } from "~/store/dialog/dialogStore";
import { useGenericDialog } from "~/composables/useGenericDialog";
import { useProSkillAssureService } from "~/composables/service/useProSkillAssureService";
import { useAssessmentSeat } from "~/composables/useAssessmentSeat";
import { useExamSession } from "~/composables/useExamSession";
import {
  examAnswerSignature,
  hasExamAnswerDraft,
  restoreExamAnswerDraft,
  saveExamAnswerDraft
} from "~/composables/examAnswerDraft";
import {
  ProSkillAssureAssessmentModel
} from "~~/package/mapper/proSkillAssure/model/assessment/ProSkillAssureAssessmentModel";
import { LazyGlobalLoading, LazyWebcamIndicator } from "#components";
import { RouteName } from "~~/package/enum/RouteName";
import DialogType from "~/components/Common/Dialog/model/DialogType";
import { useLearningWindowEventService } from "~/composables/service/useLearningWindowEventService";
import LearningConfirmPopupType from "~/components/Common/Dialog/dialog/LearningConfirmPopup/LearningConfirmPopupType";
import { RecorderDomainType } from "~~/package/mapper/recorder/model/RecorderDomainType";
import { useLearningScreenRecordingService } from "~/composables/service/useLearningScreenRecordingService";
import {
  ProSkillManagerCandidateStatusType
} from "~~/package/mapper/proSkillManager/model/candidate/ProSkillManagerCandidateStatusType";
import { useLoadingStore } from "~/store/loading/loadingStore";
import {useExternalDirectorService} from "~/composables/service/useExternalDirectorService";
import { useTaskDurationTrackingService } from "~/composables/service/useTaskDurationTrackingService";
import { useFullscreenService } from "~/composables/service/useFullscreenService";
import { useLoadingTimeoutGuide } from "~/composables/useLoadingTimeoutGuide";
import { useKeystrokeTelemetryService } from "~/composables/service/useKeystrokeTelemetryService";
import { useWebcamRecordingService } from "~/composables/service/useWebcamRecordingService";
import { shouldStartKeystrokeCapture, shouldStartWebcamCapture } from "~/composables/service/integrityCaptureGate";
import { applicationContext } from "@@/package/application/applicationContext";
import type PreSignedUrlParamsRequest from "~~/package/apis/recorde/request/PreSignedUrlParamsRequest";

definePageMeta({ sessionOwner: true });

const { t } = useI18n();

const route = useRoute();
const router = useRouter();
const userStore = useUserStore();
const learningStore = useLearningStore();
    const assessmentStore = useLearningAssessmentStore();
const dialogStore = useDialogStore();
const loadingStore = useLoadingStore();

const { openGenericAlert } = useGenericDialog();

const middle = ref();

const learningWindowEventService = useLearningWindowEventService(RecorderDomainType.PRO_SKILL_ASSURE);
const learningScreenRecordingService = useLearningScreenRecordingService();
const proSkillAssureService = useProSkillAssureService();
const externalDirectorService = useExternalDirectorService();
const taskDurationTrackingService = useTaskDurationTrackingService();
const fullscreenService = useFullscreenService();
const assessmentId = ref(parseInt(route.params.assessmentId as string));
const taskId = ref(parseInt(route.params.taskId as string));

// TSK-11198: 응시 화면 전용 무결성 캡처(키스트로크 텔레메트리 / 웹캠). initIntegrityCapture()가 시작하며,
// 웹캠은 외부감독에서만 start()된다(카메라 게이트는 initIntegrityCapture 참고). 두 서비스는 응시당 하나인
// 모듈 싱글턴이라 문항 이동으로 이 페이지가 다시 마운트되어도 같은 수집을 이어 간다 — 멈추는 곳은 실제
// 이탈(onBeforeRouteLeave)뿐이다. provide는 CodeEditor.client.vue가 캡처 확장을 주입하기 위함이다.
const keystrokeTelemetryService = useKeystrokeTelemetryService();
const webcamRecordingService = useWebcamRecordingService();
provide('keystrokeTelemetryService', keystrokeTelemetryService);
const config = { public: applicationContext.getConfig() };

// 로딩이 20초 이상 지속되면(네트워크 단발 유실 등) 새로고침 안내 팝업 노출 (TSK-10923)
const loadingTimeoutGuide = useLoadingTimeoutGuide(() => {
  dialogStore.open({
    type: DialogType.LearningConfirmPopup,
    props: { type: LearningConfirmPopupType.CONNECT_FAIL },
    confirm: { event: () => goToFailRedirect() },
  });
});

// 종료성 에러 팝업 노출 시, 그 위에 타임아웃 안내 팝업이 겹치지 않도록 가드 이동 대상 (TSK-10923)
// 시작 페이지(index)로 새로고침 형식(풀 페이지 로드)으로 이동해 상태를 깨끗이 재초기화한다.
const goToFailRedirect = () => {
  const href = userStore.isLogin()
    ? router.resolve({
        name: RouteName.LearningProSkillAssureAssessmentId,
        params: { assessmentId: route.params.assessmentId },
      }).href
    : router.resolve({ name: RouteName.Home }).href;
  window.location.href = href;
};

// TSK-11215: the seat THIS instance claimed. Per-instance on purpose — the seat itself is a
// module-level singleton, and this page mounts twice, so unmount must release our own claim
// rather than whatever is currently held (which may belong to the instance replacing us).
let claimedSeatId: string | null = null;
// 녹화 에러 구독 해제. 이 페이지가 다시 마운트되면 새 인스턴스가 구독을 넘겨받는다.
let unsubscribeRecordingError: (() => void) | null = null;
// #2260: removes this instance's expired-session task-navigation guard (see onMounted).
let removeTaskNavigationGuard: (() => void) | null = null;

onMounted(() => {
  learningStore.isNeverRun = true;
  learningStore.isChangedButNotSubmit = false;
  // #2260 (D1): while mounted, a 401 opens the session-expired popup instead of the logout cascade.
  useExamSession().activate();
  // #2260: another task can't load on an expired session — cancel the move instead of leaving the
  // candidate on a loading screen behind the popup. Every in-page task move is a taskId param change.
  removeTaskNavigationGuard = router.beforeEach((to, from) => {
    if (to.name !== from.name || to.params.taskId === from.params.taskId) return;
    const examSession = useExamSession();
    if (!examSession.isExpired()) return;
    examSession.promptRelogin();
    return false;
  });
  if (initMethods.checkValidApproach()) initMethods.getAssessmentSet();
  else initMethods.openFailPopup();
});

onBeforeMount(() => {
  loadingStore.openLoading("");
  learningStore.resetCodeData();
  learningStore.resetContestData();
  learningStore.resetAssessment();
  learningStore.resetLevelAssessment();

  // 녹화 에러 감지 및 설정 페이지로 이동
  unsubscribeRecordingError = externalDirectorService.recordingErrorHandler(() => {
    router.push({
      name: RouteName.LearningProSkillAssureAssessmentId,
      params: {assessmentId: route.params.assessmentId},
      query: {isShowSettings: 'true'}
    })
  });
});

onBeforeUnmount(() => {
  learningWindowEventService.removeEventListener(
    false,
    assessmentStore.proctoringConfig.useShortcutProctor,
    assessmentStore.proctoringConfig.useCopyPasteProctor,
    assessmentStore.proctoringConfig.useTabSwitchProctor,
    assessmentStore.proctoringConfig.useMultiMonitorProctor
  );
  taskDurationTrackingService.stopTracking();
  navigator.mediaDevices.removeEventListener('devicechange', devicechangeHandler);
  // TSK-11215: leaving the taking screen drops the seat we hold in memory. The row keeps its
  // active_seat_id — only a newer entry overwrites it — so re-entry just mints a fresh one.
  // Release only OUR claim: this page mounts twice, and Vue can mount the second instance
  // before unmounting the first, so a blind reset here wipes the survivor's seat.
  useAssessmentSeat().release(claimedSeatId);
  useExamSession().deactivate();
  removeTaskNavigationGuard?.();
  unsubscribeRecordingError?.();
});

// #2260: keep the local answer draft current while the session is expired, and after a restore
// (otherwise a later visit would bring back the older draft over newer edits).
watch(examAnswerSignature, () => {
  if (!useExamSession().isExpired() && !hasExamAnswerDraft()) return;
  try {
    saveExamAnswerDraft();
  } catch (e) {
    console.error(e);
  }
});

// ASMT-CAND-11: route-leave guard — completed/start/Dashboard/Home/NONE 외 이동 차단
onBeforeRouteLeave(async (to, from) => {
  let isPageMove = false;
  if (([RouteName.LearningProSkillAssureAssessmentIdCompleted, RouteName.LearningProSkillAssureAssessmentId, RouteName.Dashboard, RouteName.Home] as RouteName[])
    .includes(to.name as RouteName)) {
    isPageMove = true;
  } else if (assessmentStore.proSkillAssure.myInfo.status === ProSkillManagerCandidateStatusType.NONE) isPageMove = true;

  if (isPageMove) {
    loadingStore.openLoading("");
    clearInterval(assessmentStore.proSkillAssure.timer.timerIntervalId);
    learningStore.resetAll();
    fullscreenService.exitFullscreen().catch(() => {});
    dialogStore.close();
  }
  learningScreenRecordingService.stopRecording();
  // TSK-11198: 무결성 캡처는 실제로 응시 화면을 떠날 때만 끝낸다. 이동이 막히면(isPageMove=false) 응시가
  // 계속되므로 끊으면 안 된다 — 응시당 하나인 녹화라 이 화면이 다시 마운트되지 않으면 재시작되지 않는다.
  // 캡처가 시작되지 않았던 경우(감독 비활성 등)에도 stop()은 무해한 no-op이다.
  if (isPageMove) {
    const INTEGRITY_STOP_TIMEOUT_MS = 10_000;
    // Bound teardown wait: webcamRecordingService.stop() releases the camera
    // tracks before it starts uploading the recording, so racing against a timeout
    // here is safe — the camera is off either way, and the upload (if any) keeps
    // running in the background if the page/tab survives navigation.
    await Promise.race([
      Promise.allSettled([keystrokeTelemetryService.stop(), webcamRecordingService.stop()]),
      new Promise((resolve) => setTimeout(resolve, INTEGRITY_STOP_TIMEOUT_MS)),
    ]);
  }
  assessmentStore.cautionFlag.multiMonitor = true;
  dialogStore.clearMultiOption();
  return isPageMove;
});

const initAntiCheating = async () => {
  learningWindowEventService.addEventListener(
    false,
    assessmentStore.proctoringConfig.useShortcutProctor,
    assessmentStore.proctoringConfig.useCopyPasteProctor,
    assessmentStore.proctoringConfig.useTabSwitchProctor,
    assessmentStore.proctoringConfig.useMultiMonitorProctor,
    true
  );

  await externalDirectorService.initDeviceSnapshot();
  navigator.mediaDevices.addEventListener('devicechange', devicechangeHandler);
};

const devicechangeHandler = () => {
  externalDirectorService.devicechangeHandler(() => {
    router.push({
      name: RouteName.LearningProSkillAssureAssessmentId,
      params: {assessmentId: route.params.assessmentId},
      query: externalDirectorService.isCodepressoWebview ?{isShowSettings: 'true'} : {}
    })
  });
}

const initScreenRecording = async () => {
  return await learningScreenRecordingService.startRecording(RecorderDomainType.PRO_SKILL_ASSURE);
};

// TSK-11198: 화면 녹화가 실제로 시작된 직후 무결성 캡처를 시작한다. 업로드 domain은
// 화면녹화(uploadVideoFile)와 동일하게 RecorderDomainType.PRO_SKILL_ASSURE("custom_assessment")를 사용한다.
// - 키스트로크 텔레메트리: 무음(shadow) 신호이므로 화면녹화가 켜진 모든 감독(기본/외부)에서 시작한다.
// - 웹캠: 카메라 권한 팝업을 띄우므로 외부감독(useExternalMonitoringFlag)에서만 시작한다(shouldStartWebcamCapture).
//   기본감독(BASIC)은 화면녹화만 켜므로 카메라를 요청하지 않는다.
const initIntegrityCapture = () => {
  const uploadParams: PreSignedUrlParamsRequest = {
    file_name: 'keystroke-events.json',
    domain: RecorderDomainType.PRO_SKILL_ASSURE,
    domain_id: assessmentStore.proSkillAssure.assessmentId,
    user_uuid: userStore.userV2.uuid,
    env: config.public.ENVIRONMENT as string,
  };
  if (shouldStartKeystrokeCapture({
    useScreenRecording: assessmentStore.proctoringConfig.useScreenRecording,
    useExternalMonitoringFlag: assessmentStore.proSkillAssure.myInfo.useExternalMonitoringFlag,
  })) {
    keystrokeTelemetryService.start(uploadParams);
  }
  if (shouldStartWebcamCapture({
    useScreenRecording: assessmentStore.proctoringConfig.useScreenRecording,
    useExternalMonitoringFlag: assessmentStore.proSkillAssure.myInfo.useExternalMonitoringFlag,
  })) {
    // 권한 거부 시 false를 반환할 뿐 절대 throw하지 않는다 — 응시를 막지 않는다.
    webcamRecordingService.start(uploadParams);
  }
};

const initMethods = {
  checkValidApproach() {
    if (!userStore.isLogin()) methods.goToHomePage();
    else return assessmentStore.proSkillAssure.myInfo.status === ProSkillManagerCandidateStatusType.IN_PROGRESS ||
        assessmentStore.proSkillAssure.myInfo.sampleAssessment;
  },
  // ASMT-CAND-12: 재진입/새로고침 시 서버 remaining-time으로 타이머 복원 + 미제출 최선두 문제로 이동
  getAssessmentSet() {
    // TSK-11215: claim the seat on EVERY entry to the taking screen — first start, refresh,
    // browser restart, device switch. The start page's button is not enough: it is the only
    // other caller of updateStatus, so a plain refresh would leave this screen seatless and
    // lock the candidate out of saving (acceptance criterion 3).
    //
    // Safe to repeat: checkValidApproach() already guarantees status === IN_PROGRESS here, so
    // the server always takes the resume() branch — never start(), so no resource
    // re-provisioning or job re-scheduling — and resume() only checks the assessment window.
    //
    // ...but NOT safe to repeat concurrently: this page mounts twice, and each START mints a
    // fresh seat. claimSeat() dedupes so the second mount reuses the first claim instead of
    // racing it. See useAssessmentSeat.
    Promise.all([
      useProSkillAssureService().getAssessment(assessmentId.value),
      useProSkillAssureService().getAssessmentRemainingTime(assessmentId.value),
      useAssessmentSeat()
        .claimSeat(() => useProSkillAssureService().updateStatus(assessmentId.value, "START"))
        .then((id) => { claimedSeatId = id; })
    ]).then((result: [ProSkillAssureAssessmentModel, number, void]) => {
      const assessment = result[0];
      const remainingTime = result[1];
      if (assessment.setList.length === 0) {
        initMethods.openFailPopup();
      } else {
        const isFirstLoad = assessmentStore.proSkillAssure.assessment.setList?.length === 0;
        learningStore.updateProSkillAssessmentInfo(assessmentId.value, assessment, assessmentStore.proSkillAssure.myInfo, isFirstLoad ? remainingTime : undefined);
        initMethods.replaceRoute();
      }
    }).catch((e: unknown) => {
      if (initMethods.handleSessionExpired(e)) return;
      // 요청 reject(타임아웃/네트워크 유실 등) 시에도 로딩이 영구히 남지 않도록 새로고침 안내 (TSK-10923)
      initMethods.openConnectFailPopup();
    });
  },
  replaceRoute() {
    if (!taskId.value) {
      router.replace({ params: { ...route.params, taskId: learningStore.firstProSkillAssureTask.id?.toString() } });
    } else {
      initMethods.getTaskData(taskId.value);
    }
  },
  async getTaskData(taskId: number) {
    proSkillAssureService.getAssessmentTasksWithLatestCodes(assessmentId.value, learningStore.getCurrentProSkillAssureSet(taskId).templateId, taskId).then(task => {
      if (task.id === 0) {
        initMethods.openFailPopup();
      } else {
        learningStore.updateLearningData(task);
        // #2260: an answer kept across the expired-session login round-trip wins over the server copy.
        restoreExamAnswerDraft();
        if(!assessmentStore.proSkillAssure.myInfo.sampleAssessment) taskDurationTrackingService.startTracking(taskId);
        loadingStore.closeLoading();
      }
    }).catch((e :any) => {
      if (initMethods.handleSessionExpired(e)) return;
      if (e.code && e.code === "202")
        initMethods.openConnectFailPopup();
      else {
        // ASMT-CAND-06: 진행 중 권한 박탈(문제 조회 거부) → 권한 없음 팝업 후 이탈
        loadingTimeoutGuide.clear();
        dialogStore.open({
          type: DialogType.LearningConfirmPopup,
          props: {
            icon: "no",
            title: t("skillCertify-limit-permission-title"),
            content: t("skillCertify-limit-permission-description")
          },
          confirm: {
            title: t("common-ok"),
            event: () => {
              router.replace({ name: RouteName.Dashboard });
            }
          },
          close: {
            event: () => {
              router.replace({ name: RouteName.Dashboard });
            }
          }
        });
      }
    });

    if (
        assessmentStore.proctoringConfig.useScreenRecording
        && assessmentStore.recorderStream === null
        // && !externalDirectorService.isMac
    ) {
      const recodingSuccess = await initScreenRecording();
      if (recodingSuccess) {
        await fullscreenService.ensureFullscreen();
        initAntiCheating();
      }
    } else {
      await fullscreenService.ensureFullscreen();
      initAntiCheating();
    }
    // 무결성 캡처는 브라우저 화면녹화 성공 여부와 무관하게 시작한다 — 외부감독(Proctive)
    // 응시는 데스크톱 앱이 화면을 녹화하므로 위 분기를 타지 않는다(shouldStartKeystrokeCapture).
    initIntegrityCapture();
  },
  // #2260: a 401 here is the expired session, not a connection/permission problem — show the
  // session-expired popup (again, the screen cannot load without auth) instead of those popups.
  handleSessionExpired(e: unknown): boolean {
    if (!useExamSession().handleUnauthorized(e)) return false;
    loadingTimeoutGuide.clear();
    useExamSession().promptRelogin();
    return true;
  },
  openFailPopup() {
    loadingTimeoutGuide.clear();
    openGenericAlert(
      {
        title: t("common-wrong-approach-title"),
        contents: t("common-wrong-approach-content"),
      },
      {
        confirm: { event: goToFailRedirect },
        close: { event: goToFailRedirect },
      }
    );
  },
  openConnectFailPopup() {
    loadingTimeoutGuide.clear();
    dialogStore.open({
      type: DialogType.LearningConfirmPopup,
      props: {
        type: LearningConfirmPopupType.CONNECT_FAIL
      },
      confirm: {
        event: () => {}
      },
    })
  }
};

const methods = {
  goToHomePage() {
    router.replace({ path: "/" });
  }
};

const interactiveMethods = {
  onMousemove(event: MouseEvent) {
    if (learningStore.draggingActivityWidget.id !== -1 || learningStore.isChangeActivityMenuHeight) {
      learningStore.mousePosition = { x: event.clientX, y: event.clientY };
      interactiveMethods.setContentPosition();
    }
  },
  onMouseup(event: MouseEvent) {
    learningStore.isChangeActivityMenuHeight = false;
    if (learningStore.draggingActivityWidget.id !== -1) {
      const isInteractionWidget = (event.target as HTMLElement).getAttribute("is-interaction-widget");
      if (
        learningStore.draggingActivityWidget.isOrderWidget
        && learningStore.draggingActivityWidget.isSelectedCard
        && !isInteractionWidget
      ) {
        const index = learningStore.task.markOrderList.findIndex(order => order.id === learningStore.draggingActivityWidget.id);
        learningStore.task.markOrderList[index].answerIndex = -1;

        learningStore.isOrderMenuOpen = true;
        learningStore.isSelectedSelectionId = -1;
      }

      learningStore.draggingActivityWidget = {
        isOrderWidget: false,
        isSelectedCard: false,
        id: -1
      };
      learningStore.mousePosition = { x: -1, y: -1 };
    }
  },
  onTouchMove(event: TouchEvent) {
    if (learningStore.isChangeActivityMenuHeight) {
      if (event.touches && event.touches.length > 0) {
        learningStore.mousePosition = { x: event.touches[0].clientX, y: event.touches[0].clientY };
      }
      interactiveMethods.setContentPosition();
    }
  },
  onTouchEnd() {
    learningStore.isChangeActivityMenuHeight = false;
  },
  setContentPosition() {
    if (learningStore.isChangeActivityMenuHeight) {
      learningStore.contentPosition = {
        height: middle.value.$el.clientHeight,
        top: middle.value.$el.getBoundingClientRect().top
      };
    }
  }
};

const wrapStyle = computed(() => {
  if (learningStore.draggingActivityWidget.id !== -1) {
    return {
      userSelect: "none",
      cursor: "grabbing"
    };
  }
  return {};
});
</script>

<style>
@import "~/assets/scss/lecture_style.scss";
</style>